'use client';
import { useAppContext } from '@/contexts/AppContext';
import { noto_sans } from '@/globalConstants';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from 'next/link';
import React from 'react';
import { breadcrumbTrail } from '@/lib/breadcrumbs';

// The chevron asset is a 24x24 box whose glyph only spans x 9.29-16.41 / y 6.29-17.71,
// i.e. ~9px of dead space on its left and ~7.6px on its right. With MUI's 8px separator
// margins on top of that, each crumb pair spent ~40px on a 7px glyph AND sat visibly
// closer to the following label than the preceding one. Inlining the same path under a
// viewBox cropped to the glyph makes the separator exactly as wide as what you can see,
// so the spacing either side of it is symmetric and controlled purely by the margins.
const Separator = (
    <svg
        aria-hidden="true"
        focusable="false"
        viewBox="9.293 6.293 7.121 11.414"
        width="7"
        height="11"
        fill="currentColor"
        className="shrink-0 text-[#CAC5C4]"
    >
        <path d="M10.707 17.707L16.414 12L10.707 6.29297L9.29297 7.70697L13.586 12L9.29297 16.293L10.707 17.707Z" />
    </svg>
);


const BreadCrumb = ({ pathname, update }: { pathname: string, update?: boolean }) => {
    // Determine if the app is in web view mode.
    const { isAppView } = useAppContext();

    const handleClick = (e: React.MouseEvent, href: string) => {
        if (href === '/') {
            e.preventDefault();
            window.location.href = href;
        }
    };



    const trail = breadcrumbTrail(pathname);
    return (
        <Breadcrumbs
            className={`text-[14px] font-[500] [&_.MuiBreadcrumbs-ol]:items-center [&_.MuiBreadcrumbs-separator]:mx-2.5 [&_.MuiBreadcrumbs-separator]:my-0 ${isAppView ? 'hidden' : ''}`}
            separator={Separator}
        >
            {trail.map(({ name, href }, index) => {
                const isLast = index === trail.length - 1;
                return (
                    <Link
                        key={`${index}-${href}`}
                        href={href}
                        onClick={(e) => handleClick(e, href)}
                        className={`${isLast ? 'text-white' : 'text-[#CAC5C4]'} ${noto_sans.className} no-underline inline-block align-middle whitespace-nowrap overflow-hidden text-ellipsis max-w-[7.5rem] sm:max-w-[12rem] md:max-w-none`}
                    >
                        {name}
                    </Link>
                );
            })}
        </Breadcrumbs>
    );
};

export default React.memo(BreadCrumb);
